import { useState } from 'react';
import { Target, Plus, TrendingUp, CheckCircle2, Calendar, DollarSign, Pencil, Trash2, PlusCircle, Trophy, ArrowUpRight, Users } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useGoals } from '@hooks/useGoals';
import { useAccounts } from '@hooks/useAccounts';
import { useAuth } from '@hooks/useAuth';
import PageHeader from '@components/layout/PageHeader';
import Button from '@components/common/Button';
import Modal from '@components/common/Modal';
import Loading from '@components/common/Loading';
import EmptyState from '@components/common/EmptyState';
import PageTutorial from '@components/common/PageTutorial';
import CurrencyInput from '@components/common/CurrencyInput';
import DatePicker from '@components/common/DatePicker';
import { formatCurrency, formatDate } from '@utils/formatters';

const goalSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres').max(100),
  targetAmount: z.coerce.number().positive('Valor deve ser positivo'),
  currentAmount: z.coerce.number().min(0, 'Valor inicial nao pode ser negativo').default(0),
  deadline: z.string().optional(),
  category: z.string().optional(),
  icon: z.string().optional(),
  color: z.string().optional(),
  shared: z.boolean().default(false),
});

const contributionSchema = z.object({
  amount: z.coerce.number().positive('Valor deve ser positivo'),
  notes: z.string().optional(),
  accountId: z.string().optional(),
});

const GOAL_CATEGORIES = [
  { value: 'viagem', label: '✈️ Viagem' },
  { value: 'imovel', label: '🏠 Imóvel' },
  { value: 'veiculo', label: '🚗 Veículo' },
  { value: 'educacao', label: '🎓 Educação' },
  { value: 'emergencia', label: '🛡️ Emergência' },
  { value: 'aposentadoria', label: '🌴 Aposentadoria' },
  { value: 'eletronicos', label: '💻 Eletrônicos' },
  { value: 'casamento', label: '💒 Casamento' },
  { value: 'negocio', label: '💼 Negócio' },
  { value: 'outros', label: '🎯 Outros' },
];

const GOAL_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444',
  '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6',
];

const DEFAULT_ICONS = ['🎯', '✈️', '🏠', '🚗', '🎓', '🛡️', '💍', '💻', '🌴', '💼', '🎵', '🏖️'];

const getProgressColor = (pct) => {
  if (pct >= 100) return 'bg-emerald-500';
  if (pct >= 70) return 'bg-emerald-400';
  if (pct >= 30) return 'bg-yellow-400';
  return 'bg-red-400';
};

const getProgressTextColor = (pct) => {
  if (pct >= 70) return 'text-emerald-600 dark:text-emerald-400';
  if (pct >= 30) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
};

const GoalCard = ({ goal, onEdit, onDelete, onContribute }) => {
  const pct = goal.targetAmount > 0 ? Math.min(100, (goal.currentAmount / goal.targetAmount) * 100) : 0;
  const isCompleted = pct >= 100;
  const daysLeft = goal.deadline
    ? Math.ceil((new Date(goal.deadline) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl border shadow-sm overflow-hidden transition-all hover:shadow-md ${
      isCompleted
        ? 'border-emerald-300 dark:border-emerald-700'
        : 'border-gray-200 dark:border-gray-700'
    }`}>
      {/* Top accent bar */}
      <div
        className="h-1.5 w-full"
        style={{ backgroundColor: goal.color || '#6366f1' }}
      />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0"
              style={{ backgroundColor: `${goal.color || '#6366f1'}20` }}
            >
              {goal.icon || '🎯'}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white leading-tight">{goal.name}</h3>
              {goal.category && (
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {GOAL_CATEGORIES.find((c) => c.value === goal.category)?.label || goal.category}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {goal.shared && (
              <div className="flex items-center gap-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-1 rounded-full text-xs font-medium">
                <Users size={11} />
                Familia
              </div>
            )}
            {isCompleted && (
              <div className="flex items-center gap-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-2.5 py-1 rounded-full text-xs font-medium">
                <Trophy size={12} />
                Concluido!
              </div>
            )}
          </div>
        </div>

        {/* Amounts */}
        <div className="mb-3">
          <div className="flex justify-between items-baseline mb-1">
            <span className="text-xs text-gray-500 dark:text-gray-400">Poupado</span>
            <span className={`text-xs font-semibold ${getProgressTextColor(pct)}`}>
              {pct.toFixed(0)}%
            </span>
          </div>
          <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${getProgressColor(pct)}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex justify-between mt-1.5">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              {formatCurrency(goal.currentAmount || 0)}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {formatCurrency(goal.targetAmount || 0)}
            </span>
          </div>
        </div>

        {/* Deadline */}
        {goal.deadline && (
          <div className="flex items-center gap-1.5 text-xs mb-4">
            <Calendar size={12} className="text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">
              {formatDate(goal.deadline)}
              {daysLeft !== null && daysLeft > 0 && (
                <span className={`ml-1 font-medium ${daysLeft < 30 ? 'text-red-500' : 'text-gray-500'}`}>
                  · {daysLeft} dias restantes
                </span>
              )}
              {daysLeft !== null && daysLeft <= 0 && !isCompleted && (
                <span className="ml-1 font-medium text-red-500">· Prazo vencido</span>
              )}
            </span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          {!isCompleted && (
            <Button
              size="sm"
              variant="primary"
              leftIcon={<PlusCircle size={14} />}
              onClick={() => onContribute(goal)}
              className="flex-1"
            >
              Registrar Aporte
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Pencil size={14} />}
            onClick={() => onEdit(goal)}
          />
          <Button
            size="sm"
            variant="danger"
            leftIcon={<Trash2 size={14} />}
            onClick={() => onDelete(goal)}
          />
        </div>

        {/* Celebration for completed */}
        {isCompleted && (
          <div className="mt-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3 text-center">
            <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
              🎉 Parabens! Voce alcancou sua meta!
            </p>
          </div>
        )}

        {/* Contribution history for shared goals */}
        {goal.shared && <ContributionHistory contributions={goal.contributions} />}
      </div>
    </div>
  );
};

const ContributionHistory = ({ contributions = [] }) => {
  if (contributions.length === 0) return null;
  return (
    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Ultimos aportes</p>
      <div className="space-y-1.5 max-h-24 overflow-y-auto">
        {contributions.slice(-5).reverse().map((c, i) => (
          <div key={i} className="flex items-center justify-between text-xs">
            <span className="text-gray-600 dark:text-gray-400">{c.userName}</span>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(c.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Goals = () => {
  const { tenant } = useAuth();
  const isFamily = tenant?.plan === 'family';
  const { accounts = [] } = useAccounts();
  const {
    goals,
    totalGoals,
    completedGoals,
    totalTargetAmount,
    totalSaved,
    isLoading,
    create,
    update,
    remove,
    addContribution,
    isCreating,
    isUpdating,
    isDeleting,
    isAddingContribution,
  } = useGoals();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isContributionModalOpen, setIsContributionModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [sortBy, setSortBy] = useState('deadline');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(goalSchema),
    defaultValues: {
      name: '',
      targetAmount: '',
      currentAmount: 0,
      deadline: '',
      category: '',
      icon: '🎯',
      color: '#6366f1',
      shared: false,
    },
  });

  const {
    register: registerContrib,
    handleSubmit: handleSubmitContrib,
    reset: resetContrib,
    control: controlContrib,
    formState: { errors: errorsContrib },
  } = useForm({
    resolver: zodResolver(contributionSchema),
    defaultValues: { amount: '', notes: '' },
  });

  const watchedColor = watch('color');
  const watchedIcon = watch('icon');

  const handleOpenModal = (goal = null) => {
    setSelectedGoal(goal);
    if (goal) {
      reset({
        name: goal.name,
        targetAmount: goal.targetAmount,
        currentAmount: goal.currentAmount,
        deadline: goal.deadline ? goal.deadline.slice(0, 10) : '',
        category: goal.category || '',
        icon: goal.icon || '🎯',
        color: goal.color || '#6366f1',
        shared: goal.shared || false,
      });
    } else {
      reset({ name: '', targetAmount: '', currentAmount: 0, deadline: '', category: '', icon: '🎯', color: '#6366f1', shared: false });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedGoal(null);
    reset();
  };

  const onSubmit = async (data) => {
    if (selectedGoal) {
      await update({ goalId: selectedGoal.goalId, data });
    } else {
      await create(data);
    }
    handleCloseModal();
  };

  const handleContribute = (goal) => {
    setSelectedGoal(goal);
    resetContrib({ amount: '', notes: '', accountId: '' });
    setIsContributionModalOpen(true);
  };

  const onSubmitContribution = async (data) => {
    if (selectedGoal) {
      await addContribution({
        goalId: selectedGoal.goalId,
        data: {
          amount: data.amount,
          note: data.notes,
          accountId: data.accountId || undefined,
        },
      });
      setIsContributionModalOpen(false);
      setSelectedGoal(null);
      resetContrib();
    }
  };

  const handleDeleteGoal = async () => {
    if (selectedGoal) {
      await remove(selectedGoal.goalId);
      setIsDeleteModalOpen(false);
      setSelectedGoal(null);
    }
  };

  const sortedGoals = [...goals].sort((a, b) => {
    if (sortBy === 'deadline') {
      if (!a.deadline) return 1;
      if (!b.deadline) return -1;
      return new Date(a.deadline) - new Date(b.deadline);
    }
    if (sortBy === 'progress') {
      const pctA = a.targetAmount > 0 ? (a.currentAmount / a.targetAmount) * 100 : 0;
      const pctB = b.targetAmount > 0 ? (b.currentAmount / b.targetAmount) * 100 : 0;
      return pctB - pctA;
    }
    if (sortBy === 'amount') {
      return b.targetAmount - a.targetAmount;
    }
    return 0;
  });

  if (isLoading) {
    return <Loading text="Carregando suas metas..." />;
  }

  const overallPct = totalTargetAmount > 0 ? (totalSaved / totalTargetAmount) * 100 : 0;

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Sonhos e Metas"
        subtitle="Planeje e acompanhe seus objetivos financeiros"
        action={
          <Button leftIcon={<Plus size={18} />} onClick={() => handleOpenModal()}>
            Nova Meta
          </Button>
        }
      />

      <PageTutorial
        pageId="goals"
        title="Sonhos e metas"
        steps={[
          'Defina metas financeiras com valor alvo e prazo',
          'Registre contribuicoes para acompanhar o progresso de cada meta',
          'Metas compartilhadas (familia) permitem que todos os membros contribuam',
        ]}
      />

      {/* Hero Summary */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-primary-900/80 to-slate-900 text-white shadow-2xl p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-primary-500/10 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-purple-400/10 blur-2xl" />
        <div className="relative">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-6">
            <div>
              <p className="text-white/50 text-sm font-medium mb-1">Progresso Geral</p>
              <p className="text-4xl font-bold">{formatCurrency(totalSaved)}</p>
              <p className="text-white/60 text-sm mt-1">de {formatCurrency(totalTargetAmount)} em metas</p>
            </div>
            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-white/40 text-xs mb-1">Total de Metas</p>
                <p className="text-white font-bold text-2xl">{totalGoals}</p>
              </div>
              <div className="w-px bg-white/10" />
              <div className="text-center">
                <p className="text-white/40 text-xs mb-1">Concluídas</p>
                <p className="text-emerald-400 font-bold text-2xl">{completedGoals}</p>
              </div>
              <div className="w-px bg-white/10" />
              <div className="text-center">
                <p className="text-white/40 text-xs mb-1">Progresso</p>
                <p className="text-primary-300 font-bold text-2xl">{overallPct.toFixed(0)}%</p>
              </div>
            </div>
          </div>
          {/* Overall progress bar */}
          <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-primary-400 to-emerald-400 transition-all duration-700"
              style={{ width: `${Math.min(100, overallPct)}%` }}
            />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-white/40">0%</span>
            <span className="text-xs text-white/40">100%</span>
          </div>
        </div>
      </div>

      {/* Sort Controls */}
      {goals.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">Ordenar por:</span>
          {[
            { value: 'deadline', label: 'Prazo' },
            { value: 'progress', label: 'Progresso' },
            { value: 'amount', label: 'Valor' },
          ].map((opt) => (
            <button
              key={opt.value}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                sortBy === opt.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              onClick={() => setSortBy(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Goals Grid */}
      {goals.length === 0 ? (
        <EmptyState
          icon={<Target size={32} />}
          title="Nenhuma meta criada"
          description="Defina seus sonhos e objetivos financeiros. Cada poupança conta!"
          action={() => handleOpenModal()}
          actionLabel="Criar Primeira Meta"
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedGoals.map((goal) => (
            <GoalCard
              key={goal.goalId}
              goal={goal}
              onEdit={handleOpenModal}
              onDelete={(g) => { setSelectedGoal(g); setIsDeleteModalOpen(true); }}
              onContribute={handleContribute}
            />
          ))}
        </div>
      )}

      {/* Goal Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedGoal ? 'Editar Meta' : 'Nova Meta'}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseModal}>Cancelar</Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              isLoading={isCreating || isUpdating}
            >
              {selectedGoal ? 'Salvar' : 'Criar Meta'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Icon picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ícone
            </label>
            <div className="flex flex-wrap gap-2">
              {DEFAULT_ICONS.map((ico) => (
                <button
                  key={ico}
                  type="button"
                  className={`w-10 h-10 rounded-xl text-xl flex items-center justify-center border-2 transition-all ${
                    watchedIcon === ico
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                      : 'border-transparent bg-gray-100 dark:bg-gray-700 hover:border-gray-300'
                  }`}
                  onClick={() => setValue('icon', ico)}
                >
                  {ico}
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nome da Meta *
            </label>
            <input
              type="text"
              placeholder="Ex: Viagem para a Europa"
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              {...register('name')}
            />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>}
          </div>

          {/* Amounts row */}
          <div className="grid grid-cols-2 gap-3">
            <Controller
              name="targetAmount"
              control={control}
              render={({ field }) => (
                <CurrencyInput
                  label="Valor Alvo *"
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.floatValue ?? '')}
                  onBlur={field.onBlur}
                  error={errors.targetAmount?.message}
                />
              )}
            />
            <Controller
              name="currentAmount"
              control={control}
              render={({ field }) => (
                <CurrencyInput
                  label="Valor Inicial"
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.floatValue ?? '')}
                  onBlur={field.onBlur}
                  error={errors.currentAmount?.message}
                />
              )}
            />
          </div>

          {/* Deadline + Category */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <DatePicker label="Prazo" {...register('deadline')} error={errors.deadline?.message} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Categoria
              </label>
              <select
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                {...register('category')}
              >
                <option value="">Selecionar...</option>
                {GOAL_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Color picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Cor
            </label>
            <div className="flex gap-2 flex-wrap">
              {GOAL_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    watchedColor === color ? 'border-gray-900 dark:border-white scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => setValue('color', color)}
                />
              ))}
            </div>
          </div>

          {/* Shared toggle - only for family plan */}
          {isFamily && (
            <label className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                {...register('shared')}
              />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Meta compartilhada</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Todos os membros da familia podem contribuir
                </p>
              </div>
            </label>
          )}
        </div>
      </Modal>

      {/* Contribution Modal */}
      <Modal
        isOpen={isContributionModalOpen}
        onClose={() => { setIsContributionModalOpen(false); setSelectedGoal(null); resetContrib(); }}
        title={`Registrar Aporte — ${selectedGoal?.name || ''}`}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setIsContributionModalOpen(false); resetContrib(); }}>
              Cancelar
            </Button>
            <Button
              leftIcon={<ArrowUpRight size={16} />}
              onClick={handleSubmitContrib(onSubmitContribution)}
              isLoading={isAddingContribution}
            >
              Registrar
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {selectedGoal && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-500 dark:text-gray-400">Progresso atual</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {formatCurrency(selectedGoal.currentAmount)} / {formatCurrency(selectedGoal.targetAmount)}
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                <div
                  className="bg-primary-500 h-full rounded-full"
                  style={{
                    width: `${Math.min(100, selectedGoal.targetAmount > 0 ? (selectedGoal.currentAmount / selectedGoal.targetAmount) * 100 : 0)}%`
                  }}
                />
              </div>
            </div>
          )}
          <Controller
            name="amount"
            control={controlContrib}
            render={({ field }) => (
              <CurrencyInput
                label="Valor do Aporte *"
                value={field.value}
                onValueChange={(values) => field.onChange(values.floatValue ?? '')}
                onBlur={field.onBlur}
                error={errorsContrib.amount?.message}
              />
            )}
          />
          {accounts.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Debitar da conta (opcional)
              </label>
              <select
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                {...registerContrib('accountId')}
              >
                <option value="">Não debitar de nenhuma conta</option>
                {accounts.filter(a => a.isActive !== false).map(a => (
                  <option key={a.accountId} value={a.accountId}>
                    {a.icon ? `${a.icon} ` : ''}{a.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Se selecionado, cria uma transação de saída na conta escolhida.
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Observação (opcional)
            </label>
            <input
              type="text"
              placeholder="Ex: Bônus de janeiro"
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              {...registerContrib('notes')}
            />
          </div>
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setSelectedGoal(null); }}
        title="Excluir Meta"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setIsDeleteModalOpen(false); setSelectedGoal(null); }}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDeleteGoal} isLoading={isDeleting}>
              Excluir
            </Button>
          </>
        }
      >
        <p className="text-gray-600 dark:text-gray-400">
          Tem certeza que deseja excluir a meta <strong>{selectedGoal?.name}</strong>? Esta ação não pode ser desfeita.
        </p>
      </Modal>
    </div>
  );
};

export default Goals;
